import { useMemo } from 'react';
import { ChevronRight, Database, Layers, FileText, Lock, Calendar, Tag } from 'lucide-react';
import { motion } from 'motion/react';
import type { Capsule } from '../types';
import { isEncrypted } from '../lib/protection';

/**
 * Workspace browser — aggregates capsules by workspace_id and shows a card
 * grid with counts + total size + last activity + top tags. Clicking a card
 * filters the Capsules timeline to that workspace.
 *
 * Data is computed client-side from the same Capsule[] the timeline already
 * loads — no extra backend call. Fine for <500 capsules; if it grows past
 * that we add a /api/collect/workspace aggregate endpoint.
 */
export function WorkspaceBrowser({
  capsules,
  onSelectWorkspace,
}: {
  capsules: Capsule[];
  onSelectWorkspace: (workspaceId: string) => void;
}) {
  const workspaces = useMemo(() => aggregateWorkspaces(capsules), [capsules]);

  if (workspaces.length === 0) {
    return (
      <div className="glass-panel rounded-2xl p-16 text-center">
        <Database size={48} className="heartbeat" style={{ color: '#10B981', display: 'inline-block', marginBottom: 16 }} />
        <h3 className="display text-3xl mb-3">No workspaces yet</h3>
        <p className="text-base mb-8 max-w-md mx-auto" style={{ color: 'rgba(240,253,244,0.6)' }}>
          Send your first capsule from the Send tab or via the CLI. Each unique <code className="font-mono">workspace_id</code> shows up here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-10">
        <h1 className="display text-5xl mb-3">Workspaces</h1>
        <p className="text-base" style={{ color: 'rgba(240,253,244,0.6)' }}>
          {workspaces.length} workspace{workspaces.length === 1 ? '' : 's'} across {capsules.length} capsule{capsules.length === 1 ? '' : 's'}.
          Click a card to drill into its timeline.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workspaces.map((ws, i) => (
          <motion.button
            key={ws.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.25 }}
            onClick={() => onSelectWorkspace(ws.id)}
            className="glass-panel rounded-2xl p-6 text-left group transition-all hover:scale-[1.02]"
            style={{ minHeight: 220 }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-none" style={{
                background: 'linear-gradient(135deg, #10B981, #064E3B)',
              }}>
                <Layers size={18} style={{ color: '#F0FDF4' }} />
              </div>
              <ChevronRight size={18}
                className="transition-transform group-hover:translate-x-1"
                style={{ color: 'rgba(240,253,244,0.3)' }} />
            </div>

            {/* Workspace name */}
            <div className="display text-xl mb-2 truncate" title={ws.id}>{ws.id}</div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Stat icon={<FileText size={11} />} label="capsules" value={String(ws.count)} />
              <Stat icon={<Database size={11} />} label="total size" value={formatBytes(ws.totalSize)} />
              <Stat icon={<Calendar size={11} />} label="last activity" value={formatRelative(ws.lastActivity)} />
              <Stat icon={<Lock size={11} />} label="encrypted" value={String(ws.encryptedCount)} />
            </div>

            {/* Top tags */}
            {ws.topTags.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <Tag size={11} style={{ color: 'rgba(240,253,244,0.4)' }} />
                {ws.topTags.slice(0, 4).map((t) => (
                  <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded" style={{
                    background: 'rgba(16,185,129,0.1)',
                    color: '#34D399',
                  }}>{t}</span>
                ))}
                {ws.topTags.length > 4 && (
                  <span className="text-[10px]" style={{ color: 'rgba(240,253,244,0.3)' }}>+{ws.topTags.length - 4}</span>
                )}
              </div>
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-0.5" style={{ color: 'rgba(240,253,244,0.4)' }}>
        {icon}
        <span className="label" style={{ fontSize: 9 }}>{label}</span>
      </div>
      <div className="font-mono text-sm" style={{ color: '#F0FDF4' }}>{value}</div>
    </div>
  );
}

interface WorkspaceAgg {
  id: string;
  count: number;
  encryptedCount: number;
  totalSize: number;
  lastActivity: string; // ISO
  topTags: string[];
}

function aggregateWorkspaces(capsules: Capsule[]): WorkspaceAgg[] {
  const map = new Map<string, {
    count: number;
    encryptedCount: number;
    totalSize: number;
    lastActivity: string;
    tagCounts: Map<string, number>;
  }>();

  for (const c of capsules) {
    const id = c.workspace_id || '(unknown)';
    let agg = map.get(id);
    if (!agg) {
      agg = { count: 0, encryptedCount: 0, totalSize: 0, lastActivity: c.create_date, tagCounts: new Map() };
      map.set(id, agg);
    }
    agg.count++;
    // Odoo "sealed" is every capsule; count only real ML-KEM envelopes (D-3 A)
    if (isEncrypted(c.pqc_algorithm)) agg.encryptedCount++;
    agg.totalSize += c.content_size || 0;
    if (c.create_date && c.create_date > agg.lastActivity) {
      agg.lastActivity = c.create_date;
    }
    // Tags string is comma-separated; skip the workspace:* tag (redundant)
    if (c.tags) {
      for (const raw of c.tags.split(',')) {
        const t = raw.trim();
        if (!t || t.startsWith('workspace:')) continue;
        agg.tagCounts.set(t, (agg.tagCounts.get(t) || 0) + 1);
      }
    }
  }

  const out: WorkspaceAgg[] = [];
  for (const [id, agg] of map) {
    const topTags = Array.from(agg.tagCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([t]) => t);
    out.push({
      id,
      count: agg.count,
      encryptedCount: agg.encryptedCount,
      totalSize: agg.totalSize,
      lastActivity: agg.lastActivity,
      topTags,
    });
  }

  // Most active first (last activity descending)
  out.sort((a, b) => (a.lastActivity < b.lastActivity ? 1 : -1));
  return out;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  if (!iso) return '—';
  const t = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (isNaN(t.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - t.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return t.toISOString().slice(0, 10);
}
